// noinspection CssUnusedSymbol

import styled, { css, DefaultTheme } from 'styled-components/macro';
import { onlyNumbersFromStr } from 'utils';

const getUiStateColors = (
  state: 'default' | 'success' | 'warning' | 'error',
  colors: DefaultTheme['colors']
): {
  weak: string;
  medium: string;
  strong: string;
} => {
  switch (state) {
    case 'success':
      return {
        weak: colors.green['100'],
        medium: colors.green['300'],
        strong: colors.green['600'],
      };
    case 'warning':
      return {
        weak: colors.orange['100'],
        medium: colors.orange['300'],
        strong: colors.orange['600'],
      };
    case 'error':
      return {
        weak: colors.red['100'],
        medium: colors.red['300'],
        strong: colors.red['600'],
      };
    default:
      return {
        weak: colors.primary['100'],
        medium: colors.gray['300'],
        strong: colors.primary['600'],
      };
  }
};

export const SDatePickerWrapper = styled.div`
  ${({ theme }) => {
    return css`
    position: relative;
    width: 100%;

    .react-datepicker__navigation-icon::before,
    .react-datepicker__year-read-view--down-arrow,
    .react-datepicker__month-read-view--down-arrow,
    .react-datepicker__month-year-read-view--down-arrow {
      border-color: ${theme.colors.gray['200']};
      border-style: solid;
      border-width: 3px 3px 0 0;
      content: '';
      display: block;
      height: 9px;
      position: absolute;
      top: 6px;
      width: 9px;
    }
    
    .react-datepicker-wrapper {
      display: inline-block;
      padding: 0;
      border: 0;
      width: 100%;
    }

    .react-datepicker {
      font-size: ${theme.fonts['body-14'].fontSize};
      background-color: ${theme.colors.white};
      color: ${theme.colors.gray['800']};
      border: 1px solid ${theme.colors.gray['300']} !important;
      border-radius: ${theme.radiuses.xs};
      box-shadow:
        0 0 10px 6px ${theme.colors.gray['200']},
        0 4px 8px 0 ${theme.colors.gray['300']},
        0 2px 4px 0 ${theme.colors.gray['200']};
      display: inline-block;
      position: relative;
      line-height: initial;
      margin-top: ${theme.spacings.xxs};
    }

    .react-datepicker--time-only .react-datepicker__time-container {
      border-left: 0;
    }
    
    .react-datepicker--time-only .react-datepicker__time,
    .react-datepicker--time-only .react-datepicker__time-box {
      border-bottom-left-radius: ${theme.radiuses.xs};
      border-bottom-right-radius: ${theme.radiuses.xs};
    }

    .react-datepicker-popper {
      z-index: 2000;
      line-height: 0;
    }
    
    .react-datepicker-popper .react-datepicker__triangle {
      stroke: ${theme.colors.gray['300']};
    }
    
    .react-datepicker-popper[data-placement^='bottom']
      .react-datepicker__triangle {
      fill: ${theme.colors.white};
      color: ${theme.colors.white};
    }
    
    .react-datepicker-popper[data-placement^='top']
      .react-datepicker__triangle {
      fill: ${theme.colors.white};
      color: ${theme.colors.white};
    }

    .react-datepicker__header {
      text-align: center;
      background-color: ${theme.colors.white} !important;
      color: ${theme.colors.gray[800]} !important;
      border-top-left-radius: ${theme.radiuses.xs};
      padding: 8px 0;
      position: relative;
    }
    
    .react-datepicker__header--time {
      padding-bottom: 8px;
      padding-left: 5px;
      padding-right: 5px;
      border-bottom: 1px solid ${theme.colors.gray['200']};
    }
    .react-datepicker__header--time:not(.react-datepicker__header--time--only) {
      border-top-left-radius: 0;
    }
    
    .react-datepicker__header:not(.react-datepicker__header--has-time-select) {
      border-top-right-radius: ${theme.radiuses.xs};
    }
    
    .react-datepicker__year-dropdown-container--select,
    .react-datepicker__month-dropdown-container--select,
    .react-datepicker__month-year-dropdown-container--select,
    .react-datepicker__year-dropdown-container--scroll,
    .react-datepicker__month-dropdown-container--scroll,
    .react-datepicker__month-year-dropdown-container--scroll {
      display: inline-block;
      margin: 0 20px;
    }

    .react-datepicker__current-month,
    .react-datepicker-time__header,
    .react-datepicker-year-header {
      margin-top: 0;
      color: ${theme.colors.gray[800]} !important;
      text-transform: capitalize;
      font-weight: 500;
      font-size: ${theme.fonts['body-16'].fontSize};
    }

    h2.react-datepicker__current-month {
      padding: 0;
      margin: 0 0 ${theme.spacings.xs} 0;
    }

    .react-datepicker__current-month.react-datepicker__current-month--hasMonthDropdown.react-datepicker__current-month--hasYearDropdown {
      display: none;
    }
    
    .react-datepicker-time__header {
      font-size: 0;
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
      
      &::after {
        font-size: ${theme.fonts['body-16'].fontSize};
        color: ${theme.colors.gray[800]} !important;
        font-weight: 500;
        content: 'Время';
      }
    }

    .react-datepicker__navigation {
      align-items: center;
      background: none;
      display: flex;
      justify-content: center;
      text-align: center;
      cursor: pointer;
      position: absolute;
      top: 2px;
      padding: 0;
      border: none;
      z-index: 1;
      height: 32px;
      width: 32px;
      text-indent: -999em;
      overflow: hidden;
    }
    
    .react-datepicker__navigation--previous {
      left: 2px;
    }
    
    .react-datepicker__navigation--next {
      right: 2px;
    }
    
    .react-datepicker__navigation--next--with-time:not(
        .react-datepicker__navigation--next--with-today-button
      ) {
      right: 85px;
    }
    
    .react-datepicker__navigation--years {
      position: relative;
      top: 0;
      display: block;
      margin-left: auto;
      margin-right: auto;
    }
    
    .react-datepicker__navigation--years-previous {
      top: 4px;
    }
    
    .react-datepicker__navigation--years-upcoming {
      top: -4px;
    }
    
    .react-datepicker__navigation:hover *::before {
      border-color: ${theme.colors.gray['300']};
    }

    .react-datepicker__navigation-icon {
      position: relative;
      top: -1px;
      font-size: ${theme.fonts['heading-2'].fontSize};
      width: 0;
    }
    
    .react-datepicker__navigation-icon--next {
      left: -2px;
    }
    
    .react-datepicker__navigation-icon--next::before {
      transform: rotate(45deg);
      left: -7px;
    }
    
    .react-datepicker__navigation-icon--previous {
      right: -2px;
    }
    
    .react-datepicker__navigation-icon--previous::before {
      transform: rotate(225deg);
      right: -7px;
    }

    .react-datepicker__month-container {
      float: left;
    }

    .react-datepicker__year {
      margin: ${theme.spacings.s};
      text-align: center;
    }
    
    .react-datepicker__year-wrapper {
      display: flex;
      flex-wrap: wrap;
      max-width: 180px;
    }
    
    .react-datepicker__year .react-datepicker__year-text {
      display: inline-block;
      width: 4rem;
      margin: 2px;
    }

    .react-datepicker__month {
      margin: ${theme.spacings.s};
      text-align: center;
    }
    .react-datepicker__month .react-datepicker__month-text,
    .react-datepicker__month .react-datepicker__quarter-text {
      display: inline-block;
      width: 4rem;
      margin: 2px;
    }

    .react-datepicker__input-time-container {
      clear: both;
      width: 100%;
      float: left;
      margin: 5px 0 10px 15px;
      text-align: left;
    }
    
    .react-datepicker__input-time-container .react-datepicker-time__caption {
      display: inline-block;
    }

    .react-datepicker__input-time-container
      .react-datepicker-time__input-container {
      display: inline-block;
    }

    .react-datepicker__input-time-container
      .react-datepicker-time__input-container
      .react-datepicker-time__input {
      display: inline-block;
      margin-left: 10px;
    }

    .react-datepicker__input-time-container
      .react-datepicker-time__input-container
      .react-datepicker-time__input
      input {
      width: auto;
    }

    .react-datepicker__input-time-container
      .react-datepicker-time__input-container
      .react-datepicker-time__input
      input[type='time']::-webkit-inner-spin-button,
    .react-datepicker__input-time-container
      .react-datepicker-time__input-container
      .react-datepicker-time__input
      input[type='time']::-webkit-outer-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    
    .react-datepicker__input-time-container
      .react-datepicker-time__input-container
      .react-datepicker-time__input
      input[type='time'] {
      -moz-appearance: textfield;
    }
    
    .react-datepicker__input-time-container
      .react-datepicker-time__input-container
      .react-datepicker-time__delimiter {
      margin-left: 5px;
      display: inline-block;
    }

    .react-datepicker__time-container {
      float: right;
      border-left: 1px solid ${theme.colors.gray['200']};
      width: 85px;
    }
    
    .react-datepicker__time-container--with-today-button {
      display: inline;
      border: 1px solid ${theme.colors.gray['300']};
      border-radius: ${theme.radiuses.xs};
      position: absolute;
      right: -87px;
      top: 0;
    }
    
    .react-datepicker__time-container .react-datepicker__time {
      position: relative;
      background: ${theme.colors.white};
      border-bottom-right-radius: ${theme.radiuses.xs};
    }
    
    .react-datepicker__time-container
      .react-datepicker__time
      .react-datepicker__time-box {
      width: 85px;
      overflow-x: hidden;
      margin: 0 auto;
      text-align: center;
      border-bottom-right-radius: ${theme.radiuses.xs};
    }
    
    .react-datepicker__time-container
      .react-datepicker__time
      .react-datepicker__time-box
      ul.react-datepicker__time-list {
      list-style: none;
      margin: 0;
      height: calc(195px + 1.7rem / 2);
      overflow-y: scroll;
      padding-right: 0;
      padding-left: 0;
      width: 100%;
      box-sizing: content-box;
    }
    
    .react-datepicker__time-container
      .react-datepicker__time
      .react-datepicker__time-box
      ul.react-datepicker__time-list
      li.react-datepicker__time-list-item {
      height: 30px;
      padding: 5px 10px;
      white-space: nowrap;
      line-height: 20px;
      transition: padding-left ${theme.tokens.animation.duration.base};
    }
    
    .react-datepicker__time-container
      .react-datepicker__time
      .react-datepicker__time-box
      ul.react-datepicker__time-list
      li.react-datepicker__time-list-item:hover {
      padding-left: 15px;
      cursor: pointer;
      background-color: ${theme.colors.gray['100']};
    }
    
    .react-datepicker__time-container
      .react-datepicker__time
      .react-datepicker__time-box
      ul.react-datepicker__time-list
      li.react-datepicker__time-list-item--selected {
      background-color: ${theme.colors.gray['200']};
      color: ${theme.colors.gray['800']};
      font-weight: 500;
    }
    
    .react-datepicker__time-container
      .react-datepicker__time
      .react-datepicker__time-box
      ul.react-datepicker__time-list
      li.react-datepicker__time-list-item--selected:hover {
      background-color: ${theme.colors.gray['200']};
    }
    
    .react-datepicker__time-container
      .react-datepicker__time
      .react-datepicker__time-box
      ul.react-datepicker__time-list
      li.react-datepicker__time-list-item--disabled {
      color: ${theme.colors.gray['400']};
    }
    
    .react-datepicker__time-container
      .react-datepicker__time
      .react-datepicker__time-box
      ul.react-datepicker__time-list
      li.react-datepicker__time-list-item--disabled:hover {
      cursor: not-allowed;
      background-color: transparent;
    }

    .react-datepicker__week-number {
      color: ${theme.colors.gray['800']};
      display: inline-block;
      width: 1.7rem;
      line-height: 1.7rem;
      text-align: center;
      margin: 0.166rem;
    }
    
    .react-datepicker__week-number.react-datepicker__week-number--clickable {
      cursor: pointer;
    }
    
    .react-datepicker__week-number.react-datepicker__week-number--clickable:not(
        .react-datepicker__week-number--selected
      ):hover {
      border-radius: ${theme.radiuses.xs};
      background-color: ${theme.colors.gray['100']};
    }
    
    .react-datepicker__week-number--selected {
      border-radius: ${theme.radiuses.xs};
      background-color: ${theme.colors.gray['200']};
      color: ${theme.colors.gray['800']};
    }
    
    .react-datepicker__week-number--selected:hover {
      background-color: ${theme.colors.gray['200']};
    }

    .react-datepicker__day-names {
      border-bottom: 1px solid ${theme.colors.gray['200']};
      border-top: 1px solid ${theme.colors.gray['200']};
      white-space: nowrap;
      margin-bottom: -8px;
    }

    .react-datepicker__week {
      white-space: nowrap;
    }

    .react-datepicker__day-name,
    .react-datepicker__day,
    .react-datepicker__time-name {
      color: ${theme.colors.gray['800']} !important;
      display: inline-block;
      width: 1.7rem;
      line-height: 1.7rem;
      text-align: center;
      margin: 0.166rem;
    }
    
    .react-datepicker__day-name:nth-last-child(1),
    .react-datepicker__day-name:nth-last-child(2),
    .react-datepicker__day--weekend {
      color: ${theme.colors.red['800']};
    }

    .react-datepicker__day,
    .react-datepicker__month-text,
    .react-datepicker__quarter-text,
    .react-datepicker__year-text {
      cursor: pointer;
      text-transform: capitalize;
    }
    
    .react-datepicker__day--outside-month {
      color: ${theme.colors.gray['400']};
    }
    
    .react-datepicker__day:not([aria-disabled='true']):hover,
    .react-datepicker__month-text:not([aria-disabled='true']):hover,
    .react-datepicker__quarter-text:not([aria-disabled='true']):hover,
    .react-datepicker__year-text:not([aria-disabled='true']):hover {
      border-radius: ${theme.radiuses.xs};
      background-color: ${theme.colors.gray['100']};
    }
    
    .react-datepicker__day--today,
    .react-datepicker__month-text--today,
    .react-datepicker__quarter-text--today,
    .react-datepicker__year-text--today {
      font-weight: 400;
    }
    
    // https://www.figma.com/design/TgoeofA18rwTYsDa1tLYSK/%D0%BA%D0%B0%D0%B1%D0%B8%D0%BD%D0%B5%D1%82-%D0%B6%D0%B8%D1%82%D0%B5%D0%BB%D1%8F.%D1%80%D1%84?node-id=9092-212051&m=dev
    .react-datepicker__day--highlighted,
    .react-datepicker__month-text--highlighted,
    .react-datepicker__quarter-text--highlighted,
    .react-datepicker__year-text--highlighted {
      border-radius: ${theme.radiuses.xs};
      background-color: ${theme.colors.red['500']}
      color: ${theme.colors.white};
    }

    .react-datepicker__day--highlighted:not([aria-disabled='true']):hover,
    .react-datepicker__month-text--highlighted:not(
        [aria-disabled='true']
      ):hover,
    .react-datepicker__quarter-text--highlighted:not(
        [aria-disabled='true']
      ):hover,
    .react-datepicker__year-text--highlighted:not(
        [aria-disabled='true']
      ):hover {
      background-color: ${theme.colors.red['100']}
    }

    .react-datepicker__day--highlighted-custom-1,
    .react-datepicker__month-text--highlighted-custom-1,
    .react-datepicker__quarter-text--highlighted-custom-1,
    .react-datepicker__year-text--highlighted-custom-1 {
      background-color: ${theme.colors.red['500']}
    }

    .react-datepicker__day--highlighted-custom-2,
    .react-datepicker__month-text--highlighted-custom-2,
    .react-datepicker__quarter-text--highlighted-custom-2,
    .react-datepicker__year-text--highlighted-custom-2 {
      background-color: ${theme.colors.red['100']}
    }
    
    .react-datepicker__day--holidays,
    .react-datepicker__month-text--holidays,
    .react-datepicker__quarter-text--holidays,
    .react-datepicker__year-text--holidays {
      position: relative;
      border-radius: ${theme.radiuses.xs};
      color: ${theme.colors.red['800']};
    }
    
    .react-datepicker__day--holidays .overlay,
    .react-datepicker__month-text--holidays .overlay,
    .react-datepicker__quarter-text--holidays .overlay,
    .react-datepicker__year-text--holidays .overlay {
      position: absolute;
      bottom: 120%;
      left: 50%;
      border-radius: ${theme.radiuses.xs};
      padding: ${theme.radiuses.xxs} ${theme.spacings.s};
      border: 1px solid ${theme.colors.gray['300']};
      transform: translateX(-50%);
      background-color: ${theme.colors.white};
      color: ${theme.colors.gray['800']};
      box-shadow:
        0 0 10px 6px ${theme.colors.gray['200']},
        0 4px 8px 0 ${theme.colors.gray['300']},
        0 2px 4px 0 ${theme.colors.gray['200']};
      white-space: nowrap;
      visibility: hidden;
      opacity: 0;
      transition:
        visibility 0s,
        opacity 0.3s ease-in-out;
    }
    
    .react-datepicker__day--holidays:not([aria-disabled='true']):hover,
    .react-datepicker__month-text--holidays:not([aria-disabled='true']):hover,
    .react-datepicker__quarter-text--holidays:not([aria-disabled='true']):hover,
    .react-datepicker__year-text--holidays:not([aria-disabled='true']):hover {
      background-color: ${theme.colors.gray['100']};
    }
    
    .react-datepicker__day--holidays:hover .overlay,
    .react-datepicker__month-text--holidays:hover .overlay,
    .react-datepicker__quarter-text--holidays:hover .overlay,
    .react-datepicker__year-text--holidays:hover .overlay {
      visibility: visible;
      opacity: 1;
    }
    
    .react-datepicker__day--selected,
    .react-datepicker__day--in-selecting-range,
    .react-datepicker__day--in-range,
    .react-datepicker__month-text--selected,
    .react-datepicker__month-text--in-selecting-range,
    .react-datepicker__month-text--in-range,
    .react-datepicker__quarter-text--selected,
    .react-datepicker__quarter-text--in-selecting-range,
    .react-datepicker__quarter-text--in-range,
    .react-datepicker__year-text--selected,
    .react-datepicker__year-text--in-selecting-range,
    .react-datepicker__year-text--in-range {
      border-radius: ${theme.radiuses.xs};
      background-color: ${theme.colors.gray['200']};
      font-weight: 500;
    }
    
    .react-datepicker__day--selected:not([aria-disabled='true']):hover,
    .react-datepicker__day--in-selecting-range:not(
        [aria-disabled='true']
      ):hover,
    .react-datepicker__day--in-range:not([aria-disabled='true']):hover,
    .react-datepicker__month-text--selected:not([aria-disabled='true']):hover,
    .react-datepicker__month-text--in-selecting-range:not(
        [aria-disabled='true']
      ):hover,
    .react-datepicker__month-text--in-range:not([aria-disabled='true']):hover,
    .react-datepicker__quarter-text--selected:not([aria-disabled='true']):hover,
    .react-datepicker__quarter-text--in-selecting-range:not(
        [aria-disabled='true']
      ):hover,
    .react-datepicker__quarter-text--in-range:not([aria-disabled='true']):hover,
    .react-datepicker__year-text--selected:not([aria-disabled='true']):hover,
    .react-datepicker__year-text--in-selecting-range:not(
        [aria-disabled='true']
      ):hover,
    .react-datepicker__year-text--in-range:not([aria-disabled='true']):hover {
      background-color: ${theme.colors.gray['200']};
    }
    
    .react-datepicker__day--keyboard-selected,
    .react-datepicker__month-text--keyboard-selected,
    .react-datepicker__quarter-text--keyboard-selected,
    .react-datepicker__year-text--keyboard-selected {
      font-weight: 500;
      border-radius: ${theme.radiuses.xs};
      background-color: ${theme.colors.gray['200']};
      color: ${theme.colors.gray['800']};
    }
    
    .react-datepicker__day--keyboard-selected:not([aria-disabled='true']):hover,
    .react-datepicker__month-text--keyboard-selected:not(
        [aria-disabled='true']
      ):hover,
    .react-datepicker__quarter-text--keyboard-selected:not(
        [aria-disabled='true']
      ):hover,
    .react-datepicker__year-text--keyboard-selected:not(
        [aria-disabled='true']
      ):hover {
      background-color: ${theme.colors.gray['200']};
    }
    
    .react-datepicker__day--in-selecting-range:not(
        .react-datepicker__day--in-range,
        .react-datepicker__month-text--in-range,
        .react-datepicker__quarter-text--in-range,
        .react-datepicker__year-text--in-range
      ),
    .react-datepicker__month-text--in-selecting-range:not(
        .react-datepicker__day--in-range,
        .react-datepicker__month-text--in-range,
        .react-datepicker__quarter-text--in-range,
        .react-datepicker__year-text--in-range
      ),
    .react-datepicker__quarter-text--in-selecting-range:not(
        .react-datepicker__day--in-range,
        .react-datepicker__month-text--in-range,
        .react-datepicker__quarter-text--in-range,
        .react-datepicker__year-text--in-range
      ),
    .react-datepicker__year-text--in-selecting-range:not(
        .react-datepicker__day--in-range,
        .react-datepicker__month-text--in-range,
        .react-datepicker__quarter-text--in-range,
        .react-datepicker__year-text--in-range
      ) {
      background-color: ${theme.colors.primary['100']};
    }
    
    .react-datepicker__month--selecting-range
      .react-datepicker__day--in-range:not(
        .react-datepicker__day--in-selecting-range,
        .react-datepicker__month-text--in-selecting-range,
        .react-datepicker__quarter-text--in-selecting-range,
        .react-datepicker__year-text--in-selecting-range
      ),
    .react-datepicker__year--selecting-range
      .react-datepicker__day--in-range:not(
        .react-datepicker__day--in-selecting-range,
        .react-datepicker__month-text--in-selecting-range,
        .react-datepicker__quarter-text--in-selecting-range,
        .react-datepicker__year-text--in-selecting-range
      ),
    .react-datepicker__month--selecting-range
      .react-datepicker__month-text--in-range:not(
        .react-datepicker__day--in-selecting-range,
        .react-datepicker__month-text--in-selecting-range,
        .react-datepicker__quarter-text--in-selecting-range,
        .react-datepicker__year-text--in-selecting-range
      ),
    .react-datepicker__year--selecting-range
      .react-datepicker__month-text--in-range:not(
        .react-datepicker__day--in-selecting-range,
        .react-datepicker__month-text--in-selecting-range,
        .react-datepicker__quarter-text--in-selecting-range,
        .react-datepicker__year-text--in-selecting-range
      ),
    .react-datepicker__month--selecting-range
      .react-datepicker__quarter-text--in-range:not(
        .react-datepicker__day--in-selecting-range,
        .react-datepicker__month-text--in-selecting-range,
        .react-datepicker__quarter-text--in-selecting-range,
        .react-datepicker__year-text--in-selecting-range
      ),
    .react-datepicker__year--selecting-range
      .react-datepicker__quarter-text--in-range:not(
        .react-datepicker__day--in-selecting-range,
        .react-datepicker__month-text--in-selecting-range,
        .react-datepicker__quarter-text--in-selecting-range,
        .react-datepicker__year-text--in-selecting-range
      ),
    .react-datepicker__month--selecting-range
      .react-datepicker__year-text--in-range:not(
        .react-datepicker__day--in-selecting-range,
        .react-datepicker__month-text--in-selecting-range,
        .react-datepicker__quarter-text--in-selecting-range,
        .react-datepicker__year-text--in-selecting-range
      ),
    .react-datepicker__year--selecting-range
      .react-datepicker__year-text--in-range:not(
        .react-datepicker__day--in-selecting-range,
        .react-datepicker__month-text--in-selecting-range,
        .react-datepicker__quarter-text--in-selecting-range,
        .react-datepicker__year-text--in-selecting-range
      ) {
      background-color: ${theme.colors.primary['100']};
      color: ${theme.colors.gray['800']};
    }
    
    .react-datepicker__day--disabled,
    .react-datepicker__month-text--disabled,
    .react-datepicker__quarter-text--disabled,
    .react-datepicker__year-text--disabled {
      cursor: not-allowed;
      color: ${theme.colors.gray['400']} !important;
    }
    
    .react-datepicker__day--disabled .overlay,
    .react-datepicker__month-text--disabled .overlay,
    .react-datepicker__quarter-text--disabled .overlay,
    .react-datepicker__year-text--disabled .overlay {
      position: absolute;
      bottom: 70%;
      left: 50%;
      border-radius: ${theme.radiuses.xs};
      padding: ${theme.radiuses.xxs} ${theme.spacings.s};
      border: 1px solid ${theme.colors.gray['300']};
      transform: translateX(-50%);
      background-color: ${theme.colors.white};
      color: ${theme.colors.gray['800']};
      box-shadow:
        0 0 10px 6px ${theme.colors.gray['200']},
        0 4px 8px 0 ${theme.colors.gray['300']},
        0 2px 4px 0 ${theme.colors.gray['200']};
      white-space: nowrap;
      visibility: hidden;
      opacity: 0;
      transition:
        visibility 0s,
        opacity 0.3s ease-in-out;
    }

    .react-datepicker__input-container {
      position: relative;
      display: inline-block;
      width: 100%;
    }

    .react-datepicker__input-container input {
      width: 100%;
      color: ${theme.colors.gray['800']} !important;
      background-color: ${theme.colors.white};
      transition: color ${theme.tokens.animation.duration.base};
      font-size: ${theme.fonts['body-14'].fontSize};
      font-weight: 400;
      line-height: 20px;
      border-radius: ${theme.radiuses.xs};
      border: 1px solid ${getUiStateColors('default', theme.colors).medium};
      padding: ${theme.spacings.componentOnly} 
        ${+onlyNumbersFromStr(theme.spacings.s) * 3}px
        ${theme.spacings.componentOnly} ${theme.spacings.s};
      box-sizing: border-box;
      transition:
        background-color ${theme.tokens.animation.duration.base},
        color ${theme.tokens.animation.duration.base},
        border-color ${theme.tokens.animation.duration.base},
        outline ${theme.tokens.animation.duration.base};

      &::placeholder {
        color: ${theme.colors.gray['400']};
      }

      &:hover,
      &:focus {
        border-color: ${getUiStateColors('default', theme.colors).strong};
      }

      &:focus {
        outline: 4px solid ${getUiStateColors('default', theme.colors).weak};
      }

      &:disabled {
        cursor: not-allowed;
        border-color: ${theme.colors.gray['300']};
        color: ${theme.colors.gray['400']};
      }
    }

    
    .react-datepicker__year-read-view,
    .react-datepicker__month-read-view,
    .react-datepicker__month-year-read-view {
      // В самой либе при открытии выпадающего списка прописан inline-style visibility: hidden
      visibility: visible !important;
      font-size: ${theme.fonts['body-16'].fontSize};
      font-weight: 500;
      margin-bottom: 8px;
      position: relative;
    }

    .react-datepicker__month-read-view--selected-month {
      text-transform: capitalize;
    }
    
    .react-datepicker__year-read-view:hover,
    .react-datepicker__month-read-view:hover,
    .react-datepicker__month-year-read-view:hover {
      cursor: pointer;
    }
    
    .react-datepicker__year-read-view:hover
      .react-datepicker__year-read-view--down-arrow,
    .react-datepicker__year-read-view:hover
      .react-datepicker__month-read-view--down-arrow,
    .react-datepicker__month-read-view:hover
      .react-datepicker__year-read-view--down-arrow,
    .react-datepicker__month-read-view:hover
      .react-datepicker__month-read-view--down-arrow,
    .react-datepicker__month-year-read-view:hover
      .react-datepicker__year-read-view--down-arrow,
    .react-datepicker__month-year-read-view:hover
      .react-datepicker__month-read-view--down-arrow {
      border-color: ${theme.colors.gray['300']};
    }
    
    .react-datepicker__year-read-view--down-arrow,
    .react-datepicker__month-read-view--down-arrow,
    .react-datepicker__month-year-read-view--down-arrow {
      transform: rotate(135deg);
      top: 4px;
      right: -16px;
    }

    .react-datepicker__year-dropdown,
    .react-datepicker__month-dropdown,
    .react-datepicker__month-year-dropdown {
      background-color: ${theme.colors.white};
      position: absolute;
      width: 50%;
      left: 25%;
      top: 30px;
      z-index: 1;
      text-align: center;
      border-radius: ${theme.radiuses.xs};
      border: 1px solid ${theme.colors.gray['200']};
      box-shadow:
        0 0 10px 6px ${theme.colors.gray['200']},
        0 4px 8px 0 ${theme.colors.gray['300']},
        0 2px 4px 0 ${theme.colors.gray['200']};
    }
    
    .react-datepicker__year-dropdown:hover,
    .react-datepicker__month-dropdown:hover,
    .react-datepicker__month-year-dropdown:hover {
      cursor: pointer;
    }

    .react-datepicker__month-dropdown {
      height: 200px;
      overflow-y: auto;
    }

    .react-datepicker__year-dropdown--scrollable,
    .react-datepicker__month-year-dropdown--scrollable {
      height: 200px;
      overflow-y: auto;
    }

    .react-datepicker__year-option,
    .react-datepicker__month-option,
    .react-datepicker__month-year-option {
      color: ${theme.colors.gray['800']};
      line-height: 20px;
      width: 100%;
      display: block;
      margin-left: auto;
      margin-right: auto;
      padding: 5px 10px;
      text-transform: capitalize;
      transition: padding-left ${theme.tokens.animation.duration.base};
    }
    
    .react-datepicker__year-option:first-of-type,
    .react-datepicker__month-option:first-of-type,
    .react-datepicker__month-year-option:first-of-type {
      border-top-left-radius: ${theme.radiuses.xs};
      border-top-right-radius: ${theme.radiuses.xs};
    }
    
    .react-datepicker__month-option:last-of-type,
    .react-datepicker__month-year-option:last-of-type {
      -webkit-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
      user-select: none;
      border-bottom-left-radius: ${theme.radiuses.xs};
      border-bottom-right-radius: ${theme.radiuses.xs};
    }

    .react-datepicker__year-option:last-of-type {
      display: none;
    }

    .react-datepicker__year-option:hover,
    .react-datepicker__month-option:hover,
    .react-datepicker__month-year-option:hover {
      padding-left: 15px;
      background-color: ${theme.colors.gray['100']};
    }
    
    .react-datepicker__year-option:hover
      .react-datepicker__navigation--years-upcoming,
    .react-datepicker__month-option:hover
      .react-datepicker__navigation--years-upcoming,
    .react-datepicker__month-year-option:hover
      .react-datepicker__navigation--years-upcoming {
      padding-left: 15px;
      border-color: ${theme.colors.gray['300']};
    }
    
    .react-datepicker__year-option:hover
      .react-datepicker__navigation--years-previous,
    .react-datepicker__month-option:hover
      .react-datepicker__navigation--years-previous,
    .react-datepicker__month-year-option:hover
      .react-datepicker__navigation--years-previous {
      padding-left: 15px;
      border-top-color: ${theme.colors.gray['300']};
    }
    
    .react-datepicker__year-option--selected,
    .react-datepicker__month-option--selected,
    .react-datepicker__month-year-option--selected {
      position: absolute;
      left: 10px;
    }

    .react-datepicker__close-icon {
      cursor: pointer;
      background-color: transparent;
      border: 0;
      outline: 0;
      padding: 0 12px 0 0;
      position: absolute;
      top: 0;
      right: 0;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: ${theme.colors.gray['400']};
      transition: color ${theme.tokens.animation.duration.base};
      
      &:hover {
        color: ${theme.colors.gray['800']};
      }
    }
    
    .react-datepicker__close-icon::after {
      cursor: pointer;
      display: block;
      content: '';
      color: inherit;
      background: currentColor;
      mask: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M4.0014 3.05713L8.0014 7.05713L12.0014 3.05713L12.9442 3.99994L8.94421 7.99994L12.9442 11.9999L12.0014 12.9427L8.0014 8.94275L4.0014 12.9427L3.05859 11.9999L7.05859 7.99994L3.05859 3.99994L4.0014 3.05713Z" fill="currentColor"/></svg>') no-repeat center;
      mask-size: contain;
      width: 16px;
      height: 16px;
      padding: 0;
      border-radius: 0;
    }
    
    .react-datepicker__close-icon--disabled {
      cursor: not-allowed;
    }
    
    .react-datepicker__close-icon--disabled::after {
      cursor: not-allowed;
      background-color: #ccc;
    }

    .react-datepicker__today-button {
      background: #f0f0f0;
      border-top: 1px solid #aeaeae;
      cursor: pointer;
      text-align: center;
      font-weight: bold;
      padding: 5px 0;
      clear: left;
    }

    .react-datepicker__portal {
      position: fixed;
      width: 100vw;
      height: 100vh;
      background: rgba(0, 0, 0, 0.06);
      backdrop-filter: blur(5px);
      left: 0;
      top: 0;
      justify-content: center;
      align-items: center;
      display: flex;
      z-index: 2147483647;
    }
    
    .react-datepicker__portal .react-datepicker__day-name,
    .react-datepicker__portal .react-datepicker__day,
    .react-datepicker__portal .react-datepicker__time-name {
      width: 3rem;
      line-height: 3rem;
    }
    
    @media (max-width: 400px), (max-height: 550px) {
      .react-datepicker__portal .react-datepicker__day-name,
      .react-datepicker__portal .react-datepicker__day,
      .react-datepicker__portal .react-datepicker__time-name {
        width: 2rem;
        line-height: 2rem;
      }
    }
    
    .react-datepicker__portal .react-datepicker__current-month,
    .react-datepicker__portal .react-datepicker-time__header {
      font-size: ${theme.fonts['body-14'].fontSize};
      color: ${theme.colors.primary['800']};
      font-weight: 500;
    }

    .react-datepicker__children-container {
      width: 13.8rem;
      margin: ${theme.spacings.s};
      padding-right: ${theme.radiuses.xxs};
      padding-left: ${theme.radiuses.xxs};
      height: auto;
    }

    .react-datepicker__aria-live {
      position: absolute;
      clip-path: circle(0);
      border: 0;
      height: 1px;
      margin: -1px;
      overflow: hidden;
      padding: 0;
      width: 1px;
      white-space: nowrap;
    }

  `;
  }}
`;

export const SCalendarIcon = styled.div`
  position: absolute;
  top: 50%;
  right: 13px;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  cursor: pointer;
  opacity: 0.4;
  pointer-events: all;

  &:hover {
    opacity: 0.8;
  }
`;
